/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from '@express-labs/raven-tools';
import { Icon, Link } from '@express-labs/raven-ui';
import { analyze } from '../../../../helpers';
import routes from '../../../routes/routes';
import MegaMenuCategoryImages from './subcomponents/MegaMenuCategoryImages';
import MegaMenuListItem from './subcomponents/MegaMenuListItem';
import MegaMenuSubcategory from './subcomponents/MegaMenuSubcategory';
import StickyHeader from '../StickyHeader';

import s from './MegaMenu.module.scss';

export default function MegaMenu({
  className,
  displayRewardsToolTip,
  hideTopHeader = true,
  loginStatus,
  menuLabel,
  navData,
  pageYOffset,
  stickyHeaderLimited,
  topPosition,
  width,
  isScrollingDown,
}) {
  const [activeNavIndex, setActiveNavIndex] = useState(-1);

  const SCROLL_BREAKPOINT = 800;

  const handleEnter = (e) => {
    analyze({
      name: 'megaMenuHover',
    });
    const currentNavIndex = parseInt(e.currentTarget.dataset.menuIndex, 10);

    setActiveNavIndex(currentNavIndex);
  };

  const handleTouchEnd = (e) => {
    const currentNavIndex = parseInt(e.currentTarget.dataset.menuIndex, 10);

    if (currentNavIndex !== activeNavIndex) {
      e.preventDefault();
      setActiveNavIndex(currentNavIndex);
    }
  };

  const handleLeave = () => {
    setActiveNavIndex(-1);
  };

  const isStickyHeader = stickyHeaderLimited;

  function isExpanded({ itemIndex, activeItemIndex }) {
    return itemIndex === activeItemIndex;
  }

  const handleClick = () => {
    window.location.assign(routes.home.path);
  };

  const ref = useRef({ current: { clientHeight: 0 } });

  /* istanbul ignore next */
  const onEnterHoverableModal = () => {
    const { current } = ref;
    const { classList } = current;
    classList.add(s.modal);
  };

  /* istanbul ignore next */
  const onLeaveHoverableModal = () => {
    const { current } = ref;
    const { classList } = current;
    classList.remove(s.modal);
  };

  useEffect(() => {
    const { current } = ref;
    const { classList } = current;
    const dimensions = current.getBoundingClientRect();
    const globalBanner = document?.querySelector('.global-banner-container');
    const bannerHeight = globalBanner?.clientHeight ?? 10;
    const isBelowBreakpoint = dimensions?.top <= 0
      && pageYOffset > bannerHeight
      && pageYOffset <= SCROLL_BREAKPOINT;
    const isHeaderSticky = isBelowBreakpoint;
    if (stickyHeaderLimited) {
      if (isScrollingDown) {
        if (dimensions?.top <= 0 && pageYOffset <= bannerHeight) {
          classList.remove(s.desktopHeaderShadow);
          classList.remove(s.desktopHeaderAway);
          classList.remove(s.desktopHeaderSticky);
        } else if (
          isHeaderSticky) {
          classList.add(s.desktopHeaderSticky);
          classList.add(s.desktopHeaderShadow);
          classList.remove(s.desktopHeaderAway);
        } else {
          classList.add(s.desktopHeaderAway);
        }
      } else if (dimensions?.top <= 0
        && pageYOffset > bannerHeight
      ) {
        classList.add(s.desktopHeaderSticky);
        classList.add(s.desktopHeaderShadow);
        classList.remove(s.desktopHeaderAway);
      } else {
        classList.remove(s.desktopHeaderSticky);
        classList.remove(s.desktopHeaderShadow);
        classList.remove(s.desktopHeaderAway);
      }
    }
    /* Removing white space between sticky header and promo banner */
    const rootElement = document.getElementById('root');
    /* istanbul ignore if */
    if ((stickyHeaderLimited) && rootElement) rootElement.style.minHeight = '60px';
  }, [isScrollingDown, stickyHeaderLimited, pageYOffset]);

  return (
    <div
      ref={ref}
      id="raven-header-mega-menu"
      className={cn(s.desktopHeader)}
    >
      <nav
        aria-label={menuLabel}
        className={cn(s.nav, className)}
      >
        <span
          className={cn(
            s.logoContainer,
            isStickyHeader && hideTopHeader && s.logoContainerShow,
          )}
        >
          <Link
            as="button"
            theme="dark"
            onClick={handleClick}
            className={s.icon}
            aria-label="express icon"
          >
            <span className={s.logo}>
              <Icon icon="expressLogo" />
            </span>
          </Link>
        </span>

        <ol className={cn(s.menuList, ((isStickyHeader) && hideTopHeader) && s.menuListScroll)} role="menubar">
          {navData.map(({
            categoryImages, title, url, subCategories,
          }, idx) => {
            if (title) {
              return (
              // eslint-disable-next-line react/no-array-index-key
                <li className={s.primaryNav} key={`${title}-${idx}`} role="none" data-testid="category">
                  <MegaMenuListItem
                    aria-expanded={isExpanded({ itemIndex: idx, activeItemIndex: activeNavIndex })}
                    aria-haspopup={subCategories.length > 0}
                    className={cn(s.primaryNav__label)}
                    data-menu-index={idx}
                    linkLevel={1}
                    onFocus={handleTouchEnd}
                    onMouseEnter={handleEnter}
                    onMouseLeave={handleLeave}
                    onTouchEnd={handleTouchEnd}
                    url={url}
                  >
                    {title}
                  </MegaMenuListItem>
                  <div id={`root-${title.split(' ').join('-')}`} className={s.expandableContainer}>
                    {subCategories.length > 0 && (
                    <MegaMenuSubcategory
                      className={s.subCategoriesContainer}
                      parentCategoryLabel={title}
                      subCategories={subCategories}
                    />
                    )}
                    <MegaMenuCategoryImages
                      images={categoryImages.slice(0, 1)}
                      containerClassName={s.categoryImagesContainer}
                      className={s.categoryImages}
                      showImages={
                    Boolean(categoryImages.length)
                    && isExpanded({
                      itemIndex: idx,
                      activeItemIndex: activeNavIndex,
                    })
                  }
                    />
                  </div>
                </li>
              );
            }
            return null;
          })}
        </ol>
        <div className={s.overlay} />
        <div
          className={cn(s.stickyHeader, ((isStickyHeader) && hideTopHeader) && s.stickyHeaderShow)}
          aria-label="sticky-header"
        >
          <StickyHeader
            hideTopHeader={hideTopHeader}
            windowWidth={width}
            loginStatus={loginStatus}
            displayRewardsToolTip={displayRewardsToolTip}
            topPosition={topPosition}
            onEnterHoverableModal={onEnterHoverableModal}
            onLeaveHoverableModal={onLeaveHoverableModal}
          />
        </div>
      </nav>
    </div>
  );
}

MegaMenu.propTypes = {
  className: PropTypes.string,
  menuLabel: PropTypes.string,
  navData: PropTypes.arrayOf(
    PropTypes.shape({
      categoryImages: PropTypes.arrayOf(
        PropTypes.shape({
          image: PropTypes.string,
          imageAltText: PropTypes.string,
          title: PropTypes.string,
          url: PropTypes.string,
        }),
      ),
      subCategories: PropTypes.arrayOf(
        PropTypes.shape({
          columnType: PropTypes.string,
          label: PropTypes.string,
          links: PropTypes.arrayOf(
            PropTypes.shape({
              title: PropTypes.string,
              url: PropTypes.string,
            }),
          ),
        }),
      ),
      title: PropTypes.string.isRequired,
      url: PropTypes.string,
    }),
  ).isRequired,
  hideTopHeader: PropTypes.bool,
  stickyHeaderLimited: PropTypes.bool,
  width: PropTypes.number,
  displayRewardsToolTip: PropTypes.bool,
  loginStatus: PropTypes.bool,
  topPosition: PropTypes.number,
  pageYOffset: PropTypes.number,
  isScrollingDown: PropTypes.bool,
};
